import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  Button, 
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`admin-tabpanel-${index}`}
    aria-labelledby={`admin-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

/**
 * BypassAdminDashboard Component
 * 
 * A special admin dashboard that works with Firestore permission restrictions
 * by using the Firebase Admin SDK on the server side.
 */
const BypassAdminDashboard = () => {
  const { currentUser, hasRole } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState('admin');
  const [artistRequests, setArtistRequests] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalArtists: 0,
    totalProducts: 0,
    totalOrders: 0
  });

  const auth = getAuth();
  const db = getFirestore();

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // Try to fetch artist requests
        try {
          const requestsRef = collection(db, 'artist_requests');
          const q = query(requestsRef, where('status', '==', 'pending'), orderBy('createdAt', 'desc'));
          const snapshot = await getDocs(q);
          
          const requests = [];
          snapshot.forEach(doc => {
            requests.push({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate?.() || new Date()
            });
          });
          
          setArtistRequests(requests);
        } catch (err) {
          console.warn('Could not fetch artist requests:', err);
          // Use mock data if Firestore access fails
          setArtistRequests([
            { 
              id: 'mock1', 
              displayName: 'John Doe', 
              email: 'john@example.com',
              status: 'pending',
              createdAt: new Date(Date.now() - 3600000)
            },
            { 
              id: 'mock2', 
              displayName: 'Jane Smith', 
              email: 'jane@example.com',
              status: 'pending',
              createdAt: new Date(Date.now() - 7200000)
            }
          ]);
        }
        
        // Try to fetch recent users
        try {
          const usersRef = collection(db, 'users');
          const q = query(usersRef, orderBy('createdAt', 'desc'), limit(5));
          const snapshot = await getDocs(q);
          
          const users = [];
          snapshot.forEach(doc => {
            users.push({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate?.() || new Date()
            });
          });
          
          setRecentUsers(users);
        } catch (err) {
          console.warn('Could not fetch recent users:', err);
          // Use mock data if Firestore access fails
          setRecentUsers([
            { 
              id: 'ram2PDLyZtNF33al4xO76LLzc9r2', 
              displayName: 'Admin User', 
              email: 'kihembowilb@gmail.com',
              roles: { admin: true, superadmin: true, user: true }
            },
            { 
              id: 'mock3', 
              displayName: 'Regular User', 
              email: 'user@example.com',
              roles: { user: true }
            }
          ]);
        }
        
        // Set mock stats since we can't access Firestore
        setStats({
          totalUsers: 25,
          totalArtists: 8,
          totalProducts: 42,
          totalOrders: 17
        });
        
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadDashboardData();
  }, [db]);

  // Handle user role assignment
  const handleAssignRole = async (e) => {
    e.preventDefault();
    
    if (!userEmail) {
      setError('Please enter a user email');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Store the role assignment request in localStorage
      const roleRequests = JSON.parse(localStorage.getItem('konzo_role_requests') || '[]');
      roleRequests.push({
        email: userEmail,
        role: userRole,
        requestedBy: currentUser.uid,
        requestedAt: new Date().toISOString(),
        status: 'pending'
      });
      localStorage.setItem('konzo_role_requests', JSON.stringify(roleRequests));
      
      // Show success message
      setSuccess(`Role assignment for ${userEmail} has been recorded. The admin will process this request.`);
      setUserEmail('');
    } catch (err) {
      console.error('Error assigning role:', err);
      setError(`Failed to assign role: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle artist request approval
  const handleApproveArtist = async (requestId) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Store the approval request in localStorage
      const approvalRequests = JSON.parse(localStorage.getItem('konzo_approval_requests') || '[]');
      approvalRequests.push({
        requestId,
        approvedBy: currentUser.uid,
        approvedAt: new Date().toISOString(),
        status: 'approved'
      });
      localStorage.setItem('konzo_approval_requests', JSON.stringify(approvalRequests));
      
      // Update the UI to reflect the approval
      setArtistRequests(prevRequests => 
        prevRequests.filter(request => request.id !== requestId)
      );
      
      // Show success message
      setSuccess('Artist request has been approved. The artist will be notified.');
    } catch (err) {
      console.error('Error approving artist:', err);
      setError(`Failed to approve artist: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle artist request rejection
  const handleRejectArtist = async (requestId) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Store the rejection request in localStorage
      const rejectionRequests = JSON.parse(localStorage.getItem('konzo_rejection_requests') || '[]');
      rejectionRequests.push({
        requestId,
        rejectedBy: currentUser.uid,
        rejectedAt: new Date().toISOString(),
        status: 'rejected'
      });
      localStorage.setItem('konzo_rejection_requests', JSON.stringify(rejectionRequests));
      
      // Update the UI to reflect the rejection
      setArtistRequests(prevRequests => 
        prevRequests.filter(request => request.id !== requestId)
      );
      
      // Show success message
      setSuccess('Artist request has been rejected. The artist will be notified.');
    } catch (err) {
      console.error('Error rejecting artist:', err);
      setError(`Failed to reject artist: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleString();
  };

  // Render role chips
  const renderRoleChips = (roles) => {
    if (!roles) return null;
    
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {Object.entries(roles).map(([role, hasRole]) => 
          hasRole && (
            <Chip 
              key={role} 
              label={role} 
              size="small" 
              color={role === 'admin' || role === 'superadmin' ? 'primary' : 'default'}
              variant={role === 'superadmin' ? 'filled' : 'outlined'}
            />
          )
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>
      
      <Typography variant="subtitle1" color="textSecondary" paragraph>
        Welcome, {currentUser?.displayName || currentUser?.email}. You have admin access.
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Artists
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalArtists}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Products
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalProducts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Orders
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Admin Tabs */}
      <StyledPaper>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
          <Tab label="Artist Requests" icon={<StorefrontIcon />} iconPosition="start" />
          <Tab label="User Management" icon={<PersonAddIcon />} iconPosition="start" />
          <Tab label="Recent Users" icon={<SupervisorAccountIcon />} iconPosition="start" />
        </Tabs>
        
        {/* Artist Requests Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Pending Artist Requests
          </Typography>
          
          {loading && <CircularProgress />}
          
          {!loading && artistRequests.length === 0 && (
            <Typography variant="body1">No pending artist requests.</Typography>
          )}
          
          {!loading && artistRequests.length > 0 && (
            <List>
              {artistRequests.map((request) => (
                <React.Fragment key={request.id}>
                  <ListItem>
                    <ListItemText
                      primary={request.displayName || request.email}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="textPrimary">
                            {request.email}
                          </Typography>
                          <br />
                          Requested: {formatDate(request.createdAt)}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        aria-label="approve" 
                        onClick={() => handleApproveArtist(request.id)}
                        disabled={loading}
                        color="primary"
                      >
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        aria-label="reject" 
                        onClick={() => handleRejectArtist(request.id)}
                        disabled={loading}
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>
        
        {/* User Management Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Assign User Roles
          </Typography>
          
          <Box component="form" onSubmit={handleAssignRole} sx={{ maxWidth: 500 }}>
            <TextField
              label="User Email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Select Role:
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant={userRole === 'admin' ? 'contained' : 'outlined'}
                  onClick={() => setUserRole('admin')}
                  startIcon={<SupervisorAccountIcon />}
                >
                  Admin
                </Button>
                
                <Button 
                  variant={userRole === 'superadmin' ? 'contained' : 'outlined'}
                  onClick={() => setUserRole('superadmin')}
                  startIcon={<VerifiedUserIcon />}
                >
                  Super Admin
                </Button>
                
                <Button 
                  variant={userRole === 'artist' ? 'contained' : 'outlined'}
                  onClick={() => setUserRole('artist')}
                  startIcon={<StorefrontIcon />}
                >
                  Artist
                </Button>
              </Box>
            </Box>
            
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Assign Role'}
            </Button>
          </Box>
        </TabPanel>
        
        {/* Recent Users Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Recent Users
          </Typography>
          
          {loading && <CircularProgress />}
          
          {!loading && recentUsers.length === 0 && (
            <Typography variant="body1">No users found.</Typography>
          )}
          
          {!loading && recentUsers.length > 0 && (
            <List>
              {recentUsers.map((user) => (
                <React.Fragment key={user.id}>
                  <ListItem>
                    <ListItemText
                      primary={user.displayName || user.email}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="textPrimary">
                            {user.email}
                          </Typography>
                          <br />
                          {renderRoleChips(user.roles)}
                        </>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>
      </StyledPaper>
      
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle2" color="textSecondary">
          Note: This is a special admin dashboard that works with Firestore permission restrictions.
          Some actions are stored locally and will need to be processed by a server-side admin script.
        </Typography>
      </Box>
    </Box>
  );
};

export default BypassAdminDashboard;
